<template>
    <div
        v-if="assistent"
        :class="{
            'assistent-detail': true,
            'assistent-detail--show-details': showDetails,
        }"
    >
        <div class="assistent-detail__menu">
            <flex-container>
                <input-group
                    :model-value="assistent"
                    @update:model-value="handleAssistentUpdate($event)"
                >
                    <template #input>
                        <assistent-select-input />
                    </template>
                </input-group>
            </flex-container>

            <flex-container
                :padding-y="Size.lg"
                :padding-x="Size.sm"
            >
                <assistent-detail-menu-item
                    v-for="inboxFolder in InboxFolder.values"
                    :key="inboxFolder.value"
                    :inbox-folder="inboxFolder"
                    :count="counts ? counts.chatCounts[inboxFolder.value] : 0"
                    :alert-count="counts ? counts.alertCounts[inboxFolder.value] : 0"
                    :active="currentFolder?.value === inboxFolder.value"
                    @click="handleInboxFolderClick(inboxFolder)"
                />
            </flex-container>

            <horizontal-line no-margin />

            <flex-container
                v-if="!InboxFolder.SENT_EMAILS.equals(currentFolder.value)"
                :padding-y="Size.md"
                :padding-x="Size.sm"
                :gap="Size.sm"
            >
                <flex-container
                    no-wrap
                    :gap="Size.xs"
                    align-items="center"
                >
                    <svg-icon
                        :icon="LabelIcon"
                        :size="IconSize.sm"
                        color="gray-400"
                    />
                    <text-decorator bold :size="FontSize.xs">
                        <span>Labels</span>
                    </text-decorator>
                </flex-container>

                <assistent-detail-tag-item-button
                    v-for="tag in tagCategories"
                    :key="tag.value"
                    :tag="tag"
                    :active="currentTags.includes(tag)"
                    @click="handleTagClick(tag)"
                />
            </flex-container>
        </div>
        <div class="assistent-detail__chats">
            <flex-container
                color="white"
                :padding-x="Size.md"
                justify-content="space-between"
                align-items="center"
                align-content="center"
            >
                <text-decorator>
                    Gesprekken
                </text-decorator>
                <input-group v-model="orderFilter" inline>
                    <template #input>
                        <select-input
                            :items="[
                                { value: 'desc', label: 'Nieuwste' },
                                { value: 'asc', label: 'Oudste' },
                            ]"
                            variant="inline"
                            no-scroll
                        >
                            <template #active="{ bindings }">
                                <flex-container
                                    :gap="Size.xs"
                                    inline
                                    size="auto"
                                >
                                    <svg-icon
                                        :icon="BarsFilterIcon"
                                        is-custom-icon
                                        :size="IconSize.xs"
                                    />
                                    <span>{{ bindings.value.label }}</span>
                                </flex-container>
                            </template>
                            <template #item-active>
                                <div />
                            </template>
                        </select-input>
                    </template>
                </input-group>
            </flex-container>

            <div style="height: calc(100% - 80px)">
                <template v-if="!InboxFolder.SENT_EMAILS.equals(currentFolder.value)">
                    <infinite-scrollable-collection
                        v-if="props.assistentId && chatCollection"
                        ref="chatInfiniteScroll"
                        :key="props.assistentId + currentFolder.value"
                        :collection="chatCollection"
                        :params="{
                            assistent: props.assistentId,
                            'exists[interactions]': true,
                            ...tagFilters,
                        }"
                        :items-per-load="20"
                    >
                        <template #default="{ bindings }">
                            <flex-container :padding="Size.sm" :gap="Size.sm">
                                <chat-card
                                    v-for="item in bindings.items as Chat[]"
                                    :key="item.id.value"
                                    :chat="item"
                                    :assistent="assistent"
                                    :active="currentChat?.id.value === item.id.value"
                                    :mail-count="item.emails.length"
                                    :favorited="item.isFavorite"
                                    :favorite-disabled="chatsLoadingFavorite.some((someChatId) => (item.id.equals(someChatId)))"
                                    @click="handleChatClick(item as Chat)"
                                    @favorite-clicked="handleFavoriteChat($event.chat)"
                                />
                            </flex-container>
                        </template>
                        <template #placeholder>
                            <flex-container
                                :padding="Size.sm"
                                :padding-top="Size.lg"
                                :gap="Size.sm"
                                justify-content="center"
                            >
                                <text-decorator
                                    :size="FontSize.xs"
                                    color="gray-500"
                                    bold
                                >
                                    Geen chats gevonden
                                </text-decorator>
                            </flex-container>
                        </template>
                        <template #loader>
                            <flex-container
                                :padding="Size.sm"
                                :gap="Size.sm"
                            >
                                <chat-card-skeleton
                                    v-for="i in 5"
                                    :key="i"
                                />
                            </flex-container>
                        </template>
                    </infinite-scrollable-collection>
                </template>
                <template v-else>
                    <infinite-scrollable-collection
                        v-if="props.assistentId && emailCollection"
                        :key="props.assistentId + currentFolder.value"
                        :collection="emailCollection"
                        :params="{
                            'chat.assistent': props.assistentId,
                            ...emailFilters,
                        }"
                        :items-per-load="20"
                    >
                        <template #default="{ bindings }">
                            <flex-container :padding="Size.sm" :gap="Size.sm">
                                <email-card
                                    v-for="item in bindings.items"
                                    :key="item.id.id"
                                    :email="item as ChatEmail"
                                    :assistent="assistent"
                                    :active="currentEmail?.id?.value === item.id.value"
                                    :mail-count="3"
                                    @click="handleEmailClick(item as ChatEmail)"
                                />
                            </flex-container>
                        </template>
                        <template #placeholder>
                            <flex-container
                                :padding="Size.sm"
                                :padding-top="Size.lg"
                                :gap="Size.sm"
                                justify-content="center"
                            >
                                <text-decorator
                                    :size="FontSize.xs"
                                    color="gray-500"
                                    bold
                                >
                                    Geen emails gevonden
                                </text-decorator>
                            </flex-container>
                        </template>
                        <template #loader>
                            <flex-container
                                :padding="Size.sm"
                                :gap="Size.sm"
                            >
                                <chat-card-skeleton
                                    v-for="i in 5"
                                    :key="i"
                                />
                            </flex-container>
                        </template>
                    </infinite-scrollable-collection>
                </template>
            </div>
        </div>
        <div class="assistent-detail__chat">
            <flex-container
                color="white"
                :padding-x="Size.md"
                justify-content="space-between"
                align-items="center"
                align-content="center"
            >
                <template v-if="currentChat">
                    <flex-container
                        v-if="currentChat"
                        align-items="baseline"
                        :size="5"
                        :gap="Size.sm"
                    >
                        <text-decorator>
                            Gesprek
                        </text-decorator>
                        <text-decorator :size="FontSize.xs">
                            {{ formatDayTime(currentChat.createdAt) }}
                        </text-decorator>
                    </flex-container>
                    <flex-container
                        v-if="currentChat"
                        :size="5"
                        align-items="center"
                        justify-content="flex-end"
                    >
                        <button-plain-icon
                            :icon="IdCardIcon"
                            :color="showDetails ? 'primary' : 'gray-400'"
                            is-custom-icon
                            :size="IconSize.sm"
                            @click="showDetails = !showDetails"
                        />
                        <button-plain-icon
                            :icon="ArrowTurnRightIcon"
                            color="gray-400"
                            is-custom-icon
                            :size="IconSize.sm"
                            @click="forwardChatForm?.openOverlay();"
                        />
                        <button-plain-icon
                            :icon="currentChat.isFavorite || currentChatIsLoadingFavorite ? SolidStarIcon : StarIcon"
                            :color="currentChat.isFavorite ? 'yellow-600' : 'gray-400'"
                            is-custom-icon
                            :size="IconSize.sm"
                            :disabled="currentChatIsLoadingFavorite"
                            @click="handleFavoriteChat(currentChat)"
                        />
                    </flex-container>
                </template>
                <template v-else-if="currentEmail">
                    <flex-container
                        align-items="flex-start"
                        direction="column"
                        :size="8"
                        :gap="Size.xs"
                    >
                        <text-decorator>
                            {{ currentEmail.subject }}
                        </text-decorator>
                        <flex-container align-items="center" :gap="Size.sm">
                            <text-decorator :size="FontSize.xs">
                                {{ formatDayTime(currentEmail.createdAt) }}
                            </text-decorator>
                            <div class="assistent-detail__dot" />
                            <text-decorator :size="FontSize.xs">
                                Aan: {{ currentEmail.recipient }}
                            </text-decorator>
                        </flex-container>
                    </flex-container>
                    <flex-container
                        :size="2"
                        align-items="center"
                        justify-content="flex-end"
                    >
                        <button-plain-icon
                            :icon="MessagesIcon"
                            color="gray-400"
                            is-custom-icon
                            :size="IconSize.sm"
                            @click="routeToChat"
                        />
                        <button-plain-icon
                            :icon="ArrowTurnRightIcon"
                            color="gray-400"
                            is-custom-icon
                            :size="IconSize.sm"
                            @click="forwardChatEmailForm?.openOverlay();"
                        />
                    </flex-container>
                </template>
            </flex-container>
            <template v-if="assistent">
                <template v-if="currentChat">
                    <chat-interaction-list
                        :chat="currentChat"
                        :assistent="assistent"
                        :assistent-goals="assistentGoals"
                    />
                </template>
                <template v-else-if="currentEmail">
                    <div class="assistent-detail__chat__email" v-html="currentEmail.body" />
                </template>
            </template>
        </div>
        <div
            v-if="currentChat && showDetails"
            class="assistent-detail__details"
        >
            <flex-container
                :padding-x="Size.md"
                justify-content="space-between"
                align-items="center"
                align-content="center"
            >
                <text-decorator>
                    Details
                </text-decorator>
                <button-plain-icon
                    :icon="XMarkIcon"
                    color="gray-800"
                    is-custom-icon
                    :size="IconSize.sm"
                    @click="showDetails = false"
                />
            </flex-container>
            <flex-container direction="column" :padding-x="Size.md">
                <flex-container :gap="Size.xs">
                    <flex-container align-items="center" :gap="Size.xs">
                        <text-decorator
                            color="gray-800"
                            :size="FontSize.xs"
                        >
                            Chat ID
                        </text-decorator>
                        <button-plain-icon
                            v-tooltip="'Dit is het ID voor de thread bij OpenAI (Thread-ID)'"
                            :icon="CircleExclamationIcon"
                            is-custom-icon
                            color="gray-800"
                            :size="IconSize.xxs"
                        />
                    </flex-container>

                    <flex-container justify-content="flex-end" :gap="Size.xs">
                        <copy-decorator :value="currentChat.externalId">
                            <text-decorator
                                :size="FontSize.xs"
                            >
                                {{ currentChat.externalId.substring(0, 25) }}...
                            </text-decorator>
                        </copy-decorator>
                    </flex-container>
                </flex-container>

                <chat-tags-expandable-card
                    :key="currentChat.id.value"
                    :chat="currentChat"
                />

                <chat-contact-data-expandable-card
                    :key="currentChat.id.value"
                    :chat="currentChat"
                />

                <chat-emails-expandable-card
                    :key="currentChat.id.value"
                    :chat="currentChat"
                />

                <chat-note-expandable-card
                    :key="currentChat.id.value"
                    :chat="currentChat"
                />
            </flex-container>
        </div>
        <modal-dialog
            ref="forwardChatForm"
            label="Forward chat to users"
            description="Forward chat to users"
            :size="3"
        >
            <template #default="{ bindings }">
                <flex-container>
                    <flex-container align-items="center">
                        <button-block-icon
                            :icon="ArrowTurnRightIcon"
                            :color="'turquoise-500'"
                            is-custom-icon
                        />
                        <div>
                            <text-heading>
                                Chat doorsturen
                            </text-heading>
                            <text-description>
                                Ontvanger(s) toevoegen
                            </text-description>
                        </div>
                    </flex-container>
                    <input-group v-model="forwardToInternalUsers">
                        <template #label>
                            Gebruikers:
                        </template>
                        <template #input>
                            <flex-container :gap="Size.sm">
                                <multi-select-input
                                    :items="userCollection.data"
                                    searchable
                                    :search-filter="(user, search) => searchFilter(user, search)"
                                >
                                    <template #active>
                                        Kies je gebruiker
                                    </template>
                                    <template #item="{ bindings: itemBindings }">
                                        <span style="display:flex; align-items: center;">
                                            <user-avatar
                                                :placeholder="itemBindings.item.name.abbreviation"
                                                size="md"
                                                :url="itemBindings.avatar ? resizedToSizeUploadUrl(itemBindings.avatar, 'md') : ''"
                                                :alt="itemBindings.name"
                                            />
                                            <span>&nbsp;{{ itemBindings.item.name }}</span>
                                        </span>
                                    </template>
                                </multi-select-input>
                            </flex-container>
                        </template>
                    </input-group>
                    <flex-container>
                        <chip-container>
                            <chip-label
                                v-for="(userEmail, index) in forwardToInternalEmailAddresses"
                                :key="userEmail + index"
                                size="lg"
                                :background-color="'gray-100'"
                                :border-color="'gray-400'"
                            >
                                <text-decorator :color="'gray-400'">
                                    {{ userEmail }}
                                </text-decorator>
                                <button-plain-icon
                                    :size="IconSize.sm"
                                    :icon="CloseIcon"
                                    :color="'gray-400'"
                                    @click="removeEmail(userEmail as string)"
                                />
                            </chip-label>
                        </chip-container>
                    </flex-container>
                    <form-validation v-slot="{ bindings: emailValidation }">
                        <flex-container align-items="center" justify-content="space-between">
                            <text-decorator>
                                Extra e-mailadressen:
                            </text-decorator>
                            <button-text
                                :color="'green-500'"
                                :icon="AddIcon"
                                @click="externalEmailInputCount++"
                            >
                                <svg-icon
                                    :icon="AddIcon"
                                />
                            </button-text>
                        </flex-container>
                        <flex-container
                            v-for="(i, index) in externalEmailInputCount"
                            :key="i"
                            direction="column"
                            :padding="null"
                        >
                            <input-group
                                v-model="forwardToExternalEmailAddress[i - 1]"
                                :schema="string().email().nullable()"
                            >
                                <template #input>
                                    <text-input placeholder="Voer hier een extra e-mailadres in (optioneel)">
                                        <template #suffix>
                                            <flex-container :padding="Size.xs" @click="deleteExternalEmailAddress(index)">
                                                <svg-icon
                                                    :color="'gray-400'"
                                                    :icon="DeleteIcon"
                                                />
                                            </flex-container>
                                        </template>
                                    </text-input>
                                </template>
                            </input-group>
                        </flex-container>

                        <flex-container align-items="center" justify-content="flex-end">
                            <button-text
                                :color="'black'"
                                @click="bindings.overlay.closeOverlay();"
                            >
                                Annuleren
                            </button-text>
                            <button-block
                                :color="'green-500'"
                                :disabled="allForwardedEmails.length === 0 || !emailValidation.isValid.value"
                                @loading="forwardChat(bindings, $event)"
                            >
                                Doorsturen
                            </button-block>
                        </flex-container>
                    </form-validation>
                </flex-container>
            </template>
        </modal-dialog>
        <modal-dialog
            ref="forwardChatEmailForm"
            label="Forward e-mail to users"
            description="Forward e-mail to users"
            :size="3"
        >
            <template #default="{ bindings }">
                <flex-container>
                    <flex-container align-items="center">
                        <button-block-icon
                            :icon="ArrowTurnRightIcon"
                            :color="'turquoise-500'"
                            is-custom-icon
                        />
                        <div>
                            <text-heading>
                                E-mail doorsturen
                            </text-heading>
                            <text-description>
                                Ontvanger(s) toevoegen
                            </text-description>
                        </div>
                    </flex-container>
                    <input-group v-model="forwardToInternalUsers">
                        <template #label>
                            Gebruikers:
                        </template>
                        <template #input>
                            <flex-container :gap="Size.sm">
                                <multi-select-input
                                    :items="userCollection.data"
                                    searchable
                                    :search-filter="(user, search) => searchFilter(user, search)"
                                >
                                    <template #active>
                                        Kies je gebruiker
                                    </template>
                                    <template #item="{ bindings: itemBindings }">
                                        <span style="display:flex; align-items: center;">
                                            <user-avatar
                                                :placeholder="itemBindings.item.name.abbreviation"
                                                size="md"
                                                :url="itemBindings.avatar ? resizedToSizeUploadUrl(itemBindings.avatar, 'md') : ''"
                                                :alt="itemBindings.name"
                                            />
                                            <span>&nbsp;{{ itemBindings.item.name }}</span>
                                        </span>
                                    </template>
                                </multi-select-input>
                            </flex-container>
                        </template>
                    </input-group>
                    <flex-container>
                        <chip-container>
                            <chip-label
                                v-for="(userEmail, index) in forwardToInternalEmailAddresses"
                                :key="userEmail + index"
                                size="lg"
                                :background-color="'gray-100'"
                                :border-color="'gray-400'"
                            >
                                <text-decorator :color="'gray-400'">
                                    {{ userEmail }}
                                </text-decorator>
                                <button-plain-icon
                                    :size="IconSize.sm"
                                    :icon="CloseIcon"
                                    :color="'gray-400'"
                                    @click="removeEmail(userEmail)"
                                />
                            </chip-label>
                        </chip-container>
                    </flex-container>
                    <form-validation v-slot="{ bindings: emailValidation }">
                        <flex-container align-items="center" justify-content="space-between">
                            <text-decorator>
                                Extra e-mailadressen:
                            </text-decorator>
                            <button-text
                                :color="'green-500'"
                                :icon="AddIcon"
                                @click="externalEmailInputCount++"
                            >
                                <svg-icon
                                    :icon="AddIcon"
                                />
                            </button-text>
                        </flex-container>

                        <flex-container
                            v-for="(i, index) in externalEmailInputCount"
                            :key="i"
                            direction="column"
                            :padding="null"
                        >
                            <input-group
                                v-model="forwardToExternalEmailAddress[i - 1]"
                                :schema="string().email().nullable()"
                            >
                                <template #input>
                                    <text-input placeholder="Voer hier een extra e-mailadres in (optioneel)">
                                        <template #suffix>
                                            <flex-container :padding="Size.xs" @click="deleteExternalEmailAddress(index)">
                                                <svg-icon
                                                    :color="'gray-400'"
                                                    :icon="DeleteIcon"
                                                />
                                            </flex-container>
                                        </template>
                                    </text-input>
                                </template>
                            </input-group>
                        </flex-container>
                        <flex-container align-items="center" justify-content="flex-end">
                            <button-text
                                :color=" 'black'"
                                @click="bindings.overlay.closeOverlay();"
                            >
                                Annuleren
                            </button-text>
                            <button-block
                                :color="'green-500'"
                                :disabled="allForwardedEmails.length === 0 || !emailValidation.isValid.value"
                                @loading="forwardChatEmail(bindings, $event)"
                            >
                                Doorsturen
                            </button-block>
                        </flex-container>
                    </form-validation>
                </flex-container>
            </template>
        </modal-dialog>
    </div>
</template>

<script setup lang="ts">
    import LabelIcon from '@keyvue/one-material-icons/icons/label.svg?component';
    import BarsFilterIcon from '@keyvue/one-awesome-icons/icons/regular/bars-filter.svg?component';
    import StarIcon from '@keyvue/one-awesome-icons/icons/regular/star.svg?component';
    import SolidStarIcon from '@keyvue/one-awesome-icons/icons/solid/star.svg?component';
    import ArrowTurnRightIcon from '@keyvue/one-awesome-icons/icons/regular/arrow-turn-right.svg?component';
    import IdCardIcon from '@keyvue/one-awesome-icons/icons/regular/id-card.svg?component';
    import XMarkIcon from '@keyvue/one-awesome-icons/icons/regular/xmark.svg?component';
    import CircleExclamationIcon from '@keyvue/one-awesome-icons/icons/solid/circle-exclamation.svg?component';
    import MessagesIcon from '@keyvue/one-awesome-icons/icons/regular/messages.svg?component';
    import CloseIcon from '@keyvue/one-material-icons/icons/close.svg?component';
    import AddIcon from '@keyvue/one-material-icons/icons/add.svg?component';

    import { computed, onMounted, type Ref, ref } from 'vue';
    import { onBeforeRouteUpdate, useRouter } from 'vue-router';

    import {
        Assistent,
        AssistentId,
        Chat,
        ChatId,
        ChatEmail,
        ChatEmailId,
        ChatConnectGoal,
        CountChatsCommand,
        type CountChatsCommandResponse,
        ForwardChatCommand,
        ForwardChatEmailCommand,
        User,
        FavoriteChatCommand,
        UnfavoriteChatCommand,
    } from '@keyvue/one-api-client';

    import {
        ButtonBlock,
        ButtonBlockIcon,
        ButtonState,
        type ButtonStateChangedEvent,
        ButtonPlainIcon,
        ButtonText,
        ChipContainer,
        ChipLabel,
        type ExposedOverlay,
        FlexContainer,
        FontSize,
        HorizontalLine,
        IconSize,
        InputGroup,
        ModalDialog,
        MultiSelectInput,
        SelectInput,
        Size,
        SvgIcon,
        TextDecorator,
        TextDescription,
        TextHeading,
        UserAvatar,
        FormValidation,
        NotificationBlock,
        useNotify,
    } from '@keyvue/one-ui';

    import useClient from '@/services/keyvue-api/composables/useClient';
    import AssistentSelectInput from '@/ai/assistent/components/input/AssistentSelectInput.vue';
    import AssistentDetailMenuItem from '@/ai/assistent/components/button/AssistentDetailMenuItemButton.vue';
    import InboxFolder from '@/ai/assistent/types/InboxFolder';
    import AssistentDetailTagItemButton from '@/ai/assistent/components/button/AssistentDetailTagItemButton.vue';
    import InfiniteScrollableCollection from '@/services/keyvue-api/components/InfiniteScrollableCollection.vue';
    import ChatCard from '@/ai/assistent/components/card/ChatCard.vue';
    import TagCategory from '@/ai/assistent/types/TagCategory';
    import ChatInteractionList from '@/ai/assistent/components/ChatInteractionList.vue';
    import ChatTagsExpandableCard from '@/ai/assistent/components/card/ChatTagsExpandableCard.vue';
    import ChatContactDataExpandableCard from '@/ai/assistent/components/card/ChatContactDataExpandableCard.vue';
    import CopyDecorator from '@/shared/components/CopyDecorator.vue';
    import ChatNoteExpandableCard from '@/ai/assistent/components/card/ChatNoteExpandableCard.vue';
    import ChatCardSkeleton from '@/ai/assistent/components/skeleton/ChatCardSkeleton.vue';
    import EmailCard from '@/ai/assistent/components/card/EmailCard.vue';
    import ChatEmailsExpandableCard from '@/ai/assistent/components/card/ChatEmailsExpandableCard.vue';
    import { formatDayTime } from '@/shared/utils/date';
    import { resizedToSizeUploadUrl } from '@/services/keyvue-api/utils/resize';
    import { string } from 'yup';
    import useEventBus from '@/services/eventbus/composables/useEventBus';
    import ChatFavoritedEvent, { type ChatFavoritedEventParameters } from '@/ai/assistent/events/ChatFavoritedEvent';
    import ChatUnfavoritedEvent, { type ChatUnfavoritedEventParameters } from '@/ai/assistent/events/ChatUnfavoritedEvent';
    import DeleteIcon from '@keyvue/one-material-icons/icons/delete.svg?component';

    const { client, getLazyResourceCollection, currentUser } = useClient();
    const eventBus = useEventBus();
    const router = useRouter();
    const { notify } = useNotify();

    const loading = ref(true);
    const chatsLoadingFavorite = ref<ChatId[]>([]);

    const externalEmailInputCount = ref<number>(1);

    const assistent = ref<Assistent>() as Ref<Assistent>;
    const assistentGoals = ref<ChatConnectGoal[]>([]) as Ref<ChatConnectGoal[]>;

    const counts = ref<CountChatsCommandResponse>();

    const currentFolder = ref<InboxFolder>(InboxFolder.CONVERSATIONS);
    const currentChat = ref<Chat | null>();
    const currentEmail = ref<ChatEmail | null>();
    const currentTags = ref<TagCategory[]>([]);
    const showDetails = ref<boolean>(false);

    const orderFilter = ref<{ value: string, label: string }>({ value: 'desc', label: 'Nieuwste' });

    const chatCollection = getLazyResourceCollection(Chat);
    const chatInfiniteScroll = ref<{ items: Chat[] }>();

    const emailCollection = getLazyResourceCollection(ChatEmail);
    const props = defineProps<{ assistentId: string, inbox: string, id?: string }>();

    const forwardChatForm = ref<ExposedOverlay>();
    const forwardChatEmailForm = ref<ExposedOverlay>();

    const userCollection = ref<{ data: User[], total: number }>({ data: [], total: 0 });

    const forwardToInternalUsers = ref<User[]>([]);
    const forwardToExternalEmailAddress = ref<string[]>([]);

    const forwardToInternalEmailAddresses = computed(() => {
        const internalEmails = (forwardToInternalUsers.value ?? []).map(
            (user) => user.email?.value ?? '',
        );

        return [
            ...internalEmails,
        ];
    });

    const allForwardedEmails = computed(() => {
        const internalEmails = (forwardToInternalUsers.value ?? []).map(
            (user) => user.email?.value ?? '',
        );

        return [
            ...internalEmails,
            ...forwardToExternalEmailAddress.value,
        ];
    });

    const removeEmail = (email: string) => {
        forwardToInternalUsers.value = forwardToInternalUsers.value.filter((u) => u.email?.value !== email);
    };

    eventBus.subscribe(ChatFavoritedEvent, updateChatsFavorited);
    eventBus.subscribe(ChatUnfavoritedEvent, updateChatsUnfavorited);

    onMounted(async () => {
        await load(props.assistentId, props.inbox, props.id);
    });

    onBeforeRouteUpdate(async (to) => {
        const paramAssistentId = to.params.assistentId.toString();
        const paramInbox = to.params.inbox.toString();
        const paramId = to.params.id?.toString();

        if (!paramId) {
            showDetails.value = false;
        }

        await load(paramAssistentId, paramInbox, paramId);
    });

    function redirectToMain() {
        router.push({
            name: 'assistent-detail-folder',
            params: {
                ...props,
                inbox: InboxFolder.CONVERSATIONS.routeSlug,
            },
        });
    }

    const tagCategories = computed(() => {
        return TagCategory.values.filter((tagCategory) => tagCategory.value !== TagCategory.NO_CONVERSATION.value);
    });

    const tagFilters = computed(() => {
        const filters = {
            'tags.color': currentTags.value.map((currentTag) => currentTag.value),
            'order[createdAt]': orderFilter.value.value,
        };

        if (InboxFolder.CONVERSATIONS.equals(currentFolder.value)) {
            return {
                ...filters,
                excludedTag: [TagCategory.NO_CONVERSATION.value],
            };
        }

        if (InboxFolder.SPAM.equals(currentFolder.value)) {
            return {
                ...filters,
                includedTag: [TagCategory.NO_CONVERSATION.value],
            };
        }

        if (InboxFolder.FAVORITES.equals(currentFolder.value)) {
            return {
                ...filters,
                favoritedBy: currentUser.value.id.id,
            };
        }

        return filters;
    });

    const currentChatIsLoadingFavorite = computed(() => (
        chatsLoadingFavorite.value.some((item) => item.equals(currentChat.value?.id))
    ));

    const emailFilters = computed(() => {
        return {
            'order[createdAt]': orderFilter.value.value,
        };
    });

    function searchFilter(user: User | undefined, search: string | null): boolean {
        const term = search?.toLowerCase();
        if (term === undefined) {
            return true;
        }

        return (
            (user?.email?.value?.toLowerCase() ?? '').includes(term)
            || (user?.name?.toString().toLowerCase() ?? '').includes(term)
        );
    }

    function deleteExternalEmailAddress(index: number) {
        forwardToExternalEmailAddress.value.splice(index, 1);
        externalEmailInputCount.value -= 1;
    }

    function routeToChat() {
        if (currentEmail.value === null || currentEmail.value === undefined) {
            return;
        }

        router.push({
            name: 'assistent-detail-folder',
            params: {
                inbox: InboxFolder.CONVERSATIONS.value,
                id: currentEmail.value.chat.id.value,
            },
        });
    }

    async function forwardChat(bindings: any, event: ButtonStateChangedEvent) {
        if (currentChat.value === null || currentChat.value === undefined) {
            return;
        }

        if (currentUser.value.email === null) {
            return;
        }

        await client.dispatch(ForwardChatCommand.fromChat(currentChat.value, allForwardedEmails.value, currentUser.value.id));

        bindings.overlay.closeOverlay();
        event.button.setState(ButtonState.completed);
        clearEmailList();
    }

    async function forwardChatEmail(bindings: any, event: ButtonStateChangedEvent) {
        if (currentEmail.value === null || currentEmail.value === undefined) {
            return;
        }

        if (currentUser.value.email === null) {
            return;
        }

        await client.dispatch(ForwardChatEmailCommand.fromEmail(currentEmail.value, allForwardedEmails.value, currentUser.value.id));

        bindings.overlay.closeOverlay();
        event.button.setState(ButtonState.completed);
        clearEmailList();
    }

    function clearEmailList() {
        forwardToInternalUsers.value = [];
        forwardToExternalEmailAddress.value = [];
        externalEmailInputCount.value = 1;
    }

    function handleInboxFolderClick(clickedInboxFolder: InboxFolder) {
        if (clickedInboxFolder.equals(currentFolder.value)) {
            return;
        }

        currentFolder.value = clickedInboxFolder;
        currentTags.value = [];

        router.push({
            name: 'assistent-detail-folder',
            params: {
                ...props,
                inbox: clickedInboxFolder.routeSlug,
                id: null,
            },
        });
    }

    function handleAssistentUpdate(event: any) {
        if (!event?.id) return;

        if (typeof localStorage !== 'undefined') {
            localStorage.setItem('last_visisted_assistent_id', event.id.id);
        }

        router.push({
            name: 'assistent-detail',
            params: {
                assistentId: event.id,
            },
        });
    }

    function handleTagClick(tag: TagCategory) {
        if (currentTags.value.includes(tag)) {
            currentTags.value.splice(currentTags.value.indexOf(tag), 1);
        } else {
            currentTags.value.push(tag);
        }
    }

    function handleChatClick(chat: Chat) {
        currentChat.value = chat;
        currentEmail.value = null;

        router.push({
            name: 'assistent-detail-folder',
            params: {
                ...props,
                inbox: currentFolder.value.routeSlug,
                id: chat.id.id,
            },
        });
    }

    function handleEmailClick(email: ChatEmail) {
        currentChat.value = null;
        currentEmail.value = email;

        router.push({
            name: 'assistent-detail-folder',
            params: {
                inbox: currentFolder.value.routeSlug,
                id: email.id.value,
            },
        });
    }

    async function handleFavoriteChat(chat: Chat) {
        chatsLoadingFavorite.value.push(chat.id);

        let command;

        if (chat.isFavorite) {
            command = new UnfavoriteChatCommand(
                chat.id,
                currentUser.value.id,
            );
        } else {
            command = new FavoriteChatCommand(
                chat.id,
                currentUser.value.id,
            );
        }

        try {
            await client.dispatch(command);
            await loadCounts(new AssistentId(props.assistentId));

            if (chat.isFavorite) {
                eventBus.dispatch<ChatUnfavoritedEventParameters>(ChatUnfavoritedEvent, {
                    chatId: chat.id.value,
                });
            } else {
                eventBus.dispatch<ChatFavoritedEventParameters>(ChatFavoritedEvent, {
                    chatId: chat.id.value,
                });
            }
        } catch (e) {
            notify({
                component: NotificationBlock,
                props: {
                    color: 'orange-500',
                    title: 'Mislukt!',
                    text: 'Er is iets fout gegaan tijdens het wijzigigen van de favoriet status',
                    icon: StarIcon,
                    isCustomIcon: true,
                },
                duration: 2000,
            });
        }

        const index = chatsLoadingFavorite.value.findIndex((item) => (item === chat.id));
        chatsLoadingFavorite.value.splice(index, 1);
    }

    function updateChatsFavorited(event: ChatFavoritedEventParameters) {
        if (currentChat.value && currentChat.value.id.value === event.chatId) {
            currentChat.value.isFavorite = true;
        }

        if (!chatInfiniteScroll.value) {
            return;
        }

        const infiniteScrollItems = chatInfiniteScroll.value.items;

        for (let i = 0; i < infiniteScrollItems.length; i += 1) {
            if (infiniteScrollItems[i].id.value === event.chatId) {
                infiniteScrollItems[i].isFavorite = true;
            }
        }
    }

    function updateChatsUnfavorited(event: ChatFavoritedEventParameters) {
        if (currentChat.value && currentChat.value.id.value === event.chatId) {
            currentChat.value.isFavorite = false;
        }

        if (!chatInfiniteScroll.value) {
            return;
        }

        const infiniteScrollItems = chatInfiniteScroll.value.items;

        for (let i = 0; i < infiniteScrollItems.length; i += 1) {
            if (infiniteScrollItems[i].id.value === event.chatId) {
                infiniteScrollItems[i].isFavorite = false;

                if (InboxFolder.FAVORITES.equals(currentFolder.value)) {
                    infiniteScrollItems.splice(i, 1);
                }
            }
        }
    }

    async function load(assistentId: string, inbox: string, id?: string): Promise<void> {
        loading.value = true;

        assistent.value = await client.get(Assistent, new AssistentId(assistentId));

        const assistentGoalsCollection = await client.getSubResources(assistent.value, ChatConnectGoal, 'goals');
        assistentGoals.value = assistentGoalsCollection.data;

        const inboxFolder = InboxFolder.fromRouteSlug(inbox);
        if (!inboxFolder) {
            redirectToMain();
            return;
        }

        currentFolder.value = inboxFolder;

        if (id !== undefined) {
            if (!InboxFolder.SENT_EMAILS.equals(inboxFolder)) {
                currentChat.value = await client.get(Chat, new ChatId(id));
                currentEmail.value = null;
            } else {
                currentChat.value = null;
                currentEmail.value = await client.get(ChatEmail, new ChatEmailId(id));
            }
        } else {
            currentChat.value = null;
            currentEmail.value = null;
        }

        await loadCounts(new AssistentId(assistentId));
        loading.value = false;

        await loadUsers();
    }

    async function loadUsers() {
        if (!currentUser.value || !currentUser.value.company) {
            return;
        }

        const params = {
            'active': true,
            'order[name.firstName]': 'asc',
        };

        userCollection.value = await client.getSubResources(
            currentUser.value.company,
            User,
            'users',
            params,
        );
    }

    async function loadCounts(assistentId: AssistentId) {
        counts.value = await client.dispatch(new CountChatsCommand(assistentId));
    }
</script>

<style scoped lang="scss">
    .assistent-detail {
        display: grid;
        grid-template-columns: 20% 25% 55%;
        grid-template-rows: 100%;
        height: 100%;
        width: 100%;

        &--show-details {
            grid-template-columns: 20% 25% 35% 20%;
        }

        &__menu {
            height: 100%;
            background-color: white;
            padding: size(sm);
            box-sizing: border-box;
            border-right: 1px solid var(--theme-colors-default--gray-300);
            overflow-y: scroll;
        }

        &__chats {
            height: 100%;
            background-color: var(--theme-colors-default--gray-100);
            box-sizing: border-box;
            border-right: 1px solid var(--theme-colors-default--gray-300);

            & > div:first-child {
                height: 80px;
                border-bottom: 1px solid var(--theme-colors-default--gray-300)
            }
        }

        &__chat {
            height: 100%;
            background-color: var(--theme-colors-default--gray-50);
            box-sizing: border-box;
            border-right: 1px solid var(--theme-colors-default--gray-300);

            & > div:first-child {
                height: 80px;
                border-bottom: 1px solid var(--theme-colors-default--gray-300)
            }

            &__email {
                height: calc(100% - 80px);
                overflow-y: scroll;
            }
        }

        &__details {
            height: 100%;
            background-color: var(--theme-colors-default--white);
            box-sizing: border-box;
            overflow-y: scroll;
            padding-bottom: size(xl);

            & > div:first-child {
                height: 80px;
            }
        }

        &__dot {
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: color(gray-400);
        }
    }
</style>
